import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminMessengerStore } from '../stores/adminMessenger.store';
import { parseTelegramDialogKey, telegramDialogKey } from '../../domain/entities/TelegramMessenger';

export const DIALOGS_POLL_INTERVAL_MS = 30_000;
export const MESSAGES_POLL_INTERVAL_MS = 4_000;
export const MESSAGES_FULL_REFRESH_EVERY_POLLS = 15;

export const useAdminMessenger = () => {
  const store = useAdminMessengerStore();
  const { accountUuid, dialogKey, dialogs, loadAccounts, selectAccount, loadDialogs, selectDialog, loadMessages, refreshMessages, loadSettings, reset } = store;
  const [searchParams, setSearchParams] = useSearchParams();
  const accountParam = searchParams.get('account') ?? undefined;
  const peerRef = parseTelegramDialogKey(searchParams.get('peer') ?? undefined);
  const peerParam = peerRef ? telegramDialogKey(peerRef) : undefined;
  const dialogLoaded = dialogs.some((d) => telegramDialogKey(d) === dialogKey);

  useEffect(() => {
    loadAccounts();
    return () => reset();
  }, [loadAccounts, reset]);

  useEffect(() => {
    selectAccount(accountParam);
    selectDialog(peerParam);
  }, [accountParam, peerParam, selectAccount, selectDialog]);

  useEffect(() => {
    if (!accountUuid) return undefined;
    loadDialogs();
    loadSettings();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadDialogs(true);
    }, DIALOGS_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [accountUuid, loadDialogs, loadSettings]);

  useEffect(() => {
    if (dialogLoaded) loadMessages();
  }, [dialogKey, dialogLoaded, loadMessages]);

  useEffect(() => {
    if (!accountUuid || !dialogKey || !dialogLoaded) return undefined;
    let polls = 0;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      polls += 1;
      refreshMessages({ full: polls % MESSAGES_FULL_REFRESH_EVERY_POLLS === 0 });
    }, MESSAGES_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [accountUuid, dialogKey, dialogLoaded, refreshMessages]);

  const openAccount = (uuid?: string) => setSearchParams(uuid ? { account: uuid } : {});
  const openDialog = (key?: string) => {
    if (!accountUuid) return;
    setSearchParams(key ? { account: accountUuid, peer: key } : { account: accountUuid });
  };

  return { ...store, openAccount, openDialog };
};
